<template>
  <GenerativeFlux
    v-if="currentPageData"
    :key="currentPageData.toolName"
    :toolName="currentPageData.toolName"
    :toolTitle="currentPageData.toolTitle"
    :sampleConfig="currentPageData.sampleConfig"
    :seoTitle="currentPageData.seo?.title"
    :seoDescription="currentPageData.seo?.description"
    :seoUrl="currentPageData.seo?.url"
    :pageStaticConfig="currentPageData.pageStaticConfig"
    :toolPageConfig="currentPageData.toolPageConfig"
  />
  <div v-else class="page-root">
    <main id="ai-tools" class="main-content px-4 py-1">
      <div
        class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch min-h-0 lg:h-[calc(100vh-92px)] mt-3"
      >
        <UCard
          :ui="{
            header: 'h-auto border-0',
            body: 'flex flex-col h-full min-h-0 overflow-hidden border-0 !py-1',
            footer: 'h-auto mt-auto',
          }"
          class="min-w-0 min-h-0 col-span-1 lg:col-span-1 w-full h-full flex flex-col"
        >
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-x-4 gap-y-2">
              <div class="h-5 w-36 rounded bg-white/10 animate-pulse"></div>
              <div class="h-8 w-28 rounded-md bg-white/10 animate-pulse"></div>
            </div>
          </template>

          <div class="flex-1 overflow-hidden px-1 animate-pulse">
            <div class="mb-6 h-10 w-full rounded-lg bg-white/10"></div>
            <div class="mb-6 rounded-xl border border-primary/40 p-2 bg-transparent">
              <div class="h-4 w-32 rounded bg-white/10 mb-3"></div>
              <div class="h-24 w-full rounded-lg bg-white/10"></div>
              <div class="mt-3 flex justify-end">
                <div class="h-3 w-16 rounded bg-white/10"></div>
              </div>
            </div>
            <div class="mb-6 flex items-center justify-between">
              <div class="h-4 w-28 rounded bg-white/10"></div>
              <div class="h-8 w-32 rounded-md bg-white/10"></div>
            </div>
            <div class="mb-6 flex items-center justify-between">
              <div class="h-4 w-24 rounded bg-white/10"></div>
              <div class="h-8 w-24 rounded-md bg-white/10"></div>
            </div>
            <div class="mb-6 h-36 w-full rounded-xl border border-white/10 bg-white/5 p-4">
              <div class="h-4 w-32 rounded bg-white/10 mb-4"></div>
              <div class="h-20 w-full rounded-lg bg-white/10"></div>
            </div>
            <div class="mb-6 flex items-center justify-between">
              <div class="h-4 w-36 rounded bg-white/10"></div>
              <div class="h-5 w-10 rounded-full bg-white/10"></div>
            </div>
          </div>

          <template #footer>
            <div class="space-y-4 animate-pulse">
              <div class="h-11 w-full rounded-lg bg-white/10"></div>
              <div class="mx-auto h-3 w-44 rounded bg-white/10"></div>
            </div>
          </template>
        </UCard>

        <UCard
          class="flex flex-col min-h-0 col-span-1 lg:col-span-2 h-full"
          :ui="{
            header: 'border-0',
            body: 'h-full min-h-0 overflow-y-auto py-1 sm:py-1 custom-scroll',
            footer: 'py-0 h-auto',
          }"
        >
          <template #header>
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between w-full animate-pulse">
              <div class="h-5 w-32 rounded bg-white/10"></div>
              <div class="mt-3 sm:mt-0 flex gap-2">
                <div class="h-8 w-8 rounded-md bg-white/10"></div>
                <div class="h-8 w-8 rounded-md bg-white/10"></div>
                <div class="h-8 w-8 rounded-md bg-white/10"></div>
              </div>
            </div>
          </template>

          <div class="flex flex-col gap-4 h-full animate-pulse">
            <div
              v-for="idx in 3"
              :key="`tool-task-skeleton-${idx}`"
              class="rounded-xl border border-white/10 bg-white/5 p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <div class="h-4 w-40 rounded bg-white/10"></div>
                <div class="h-4 w-10 rounded bg-white/10"></div>
              </div>
              <div class="h-40 w-full rounded-lg bg-white/10 mb-3"></div>
              <div class="h-3 w-11/12 rounded bg-white/10 mb-2"></div>
              <div class="h-3 w-3/4 rounded bg-white/10"></div>
            </div>
          </div>
        </UCard>
      </div>
    </main>
  </div>
</template>

<script setup>
import GenerativeFlux from "@/components/ai-tools/GenerativeFlux.vue";
import { useRoute } from "vue-router";
import { toSiteUrl } from "@/utils/aiCatalogData";
import { resolvePublicR2Url } from "@/utils/r2Url";

definePageMeta({ layout: "default-new", ssr: false });

const route = useRoute();

const targetPath = computed(() => {
  const slugParams = route.params.slug;
  return Array.isArray(slugParams) ? "/" + slugParams.join("/") : "";
});
const pageDataKey = computed(() => `pageData-${targetPath.value}`);

const r2CachedFetch = useR2CachedFetch();

const { data: pageDataResult } = await useAsyncData(
  pageDataKey,
  async () => {
    const key = pageDataKey.value;
    if (!targetPath.value) {
      return { key, data: null, failed: true };
    }

    const cleanPath = targetPath.value.replace(/\/$/, "");
    const apiUrl = `/api/r2/ai/pages/products${cleanPath}.json`;

    try {
      const response = await r2CachedFetch(apiUrl);

      if (!response || !response.en) {
        return { key, data: null, failed: true };
      }

      return {
        key,
        failed: false,
        data: {
          ...response.en,
          seo: {
            ...(response.en.seo || {}),
            url: toSiteUrl(response.url),
          },
          coverUrl: resolvePublicR2Url(response.coverUrl),
          toolPageConfig: response.toolPageConfig,
        },
      };
    } catch (error) {
      console.error("Fetch error:", error);
      return { key, data: null, failed: true };
    }
  },
  {
    watch: [targetPath],
    default: () => ({ key: "", data: null, failed: false }),
    lazy: true,
  }
);

const isLoadedCurrentPageData = computed(() => {
  return pageDataResult.value?.key === pageDataKey.value;
});

const currentPageData = computed(() => {
  return isLoadedCurrentPageData.value ? pageDataResult.value?.data : null;
});

const shouldShowPageNotFound = computed(() => {
  return Boolean(
    isLoadedCurrentPageData.value &&
      (pageDataResult.value?.failed || !pageDataResult.value?.data)
  );
});

watchEffect(() => {
  if (!shouldShowPageNotFound.value) return;
  showError(
    createError({
      statusCode: 404,
      statusMessage: "Page Not Found",
      fatal: true,
    })
  );
});
</script>
